import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { fileToDataUrl, captureVideoFrames, getVideoDuration } from "@/lib/media";
import { VIDEO_FILTERS, filterCss } from "@/lib/videoFilters";
import MusicPicker from "@/components/MusicPicker";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  X, SwitchCamera, Zap, ZapOff, Music, Wand2, Circle, Square, Pause, Play,
  Image as ImageIcon, Check, ArrowLeft, Scissors, Volume2, Mic2, Hash, Globe, Users, Lock, Timer, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { compressionHint } from "@/lib/format";

const DURATIONS = [
  { key: 15, label: "15s" },
  { key: 30, label: "30s" },
  { key: 60, label: "60s" },
  { key: 600, label: "10min" },
];
const COUNTDOWNS = [0, 3, 10];
const VISIBILITIES = [
  { key: "public", label: "Public", icon: Globe },
  { key: "friends", label: "Amis", icon: Users },
  { key: "private", label: "Privé", icon: Lock },
];

const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const CreateStudio = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("capture"); // capture | edit | publish

  // Camera / recording
  const liveRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const monitorRef = useRef(null); // live music monitor audio
  const [permError, setPermError] = useState("");
  const [facing, setFacing] = useState("user");
  const [torch, setTorch] = useState(false);
  const [filterKey, setFilterKey] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [durationLimit, setDurationLimit] = useState(60);
  const [countdown, setCountdown] = useState(0);
  const [countingNum, setCountingNum] = useState(0);
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  // Media result
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState(null);

  // Edit
  const editVideoRef = useRef(null);
  const editAudioRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [trim, setTrim] = useState([0, 0]);
  const [frames, setFrames] = useState([]);
  const [cover, setCover] = useState(null);
  const [track, setTrack] = useState(null);
  const [soundVol, setSoundVol] = useState(0.8);
  const [origVol, setOrigVol] = useState(1);
  const [musicOpen, setMusicOpen] = useState(false);

  // Publish
  const [caption, setCaption] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [busy, setBusy] = useState(false);

  const fileRef = useRef(null);

  // ---------- Camera lifecycle ----------
  const stopStream = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  const startCamera = useCallback(async (facingMode) => {
    stopStream();
    setPermError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode }, audio: true });
      streamRef.current = stream;
      if (liveRef.current) { liveRef.current.srcObject = stream; liveRef.current.muted = true; await liveRef.current.play().catch(() => {}); }
    } catch (e) {
      setPermError("Accès à la caméra ou au micro refusé. Autorisez les permissions dans votre navigateur, ou importez une vidéo depuis la galerie.");
    }
  }, [stopStream]);

  useEffect(() => {
    if (phase === "capture") startCamera(facing);
    return () => { if (phase === "capture") { /* keep until unmount */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => () => { stopStream(); if (monitorRef.current) monitorRef.current.pause(); if (timerRef.current) clearInterval(timerRef.current); }, [stopStream]);

  const flip = () => { const n = facing === "user" ? "environment" : "user"; setFacing(n); setTorch(false); startCamera(n); };

  const toggleTorch = async () => {
    const tr = streamRef.current?.getVideoTracks?.()[0];
    if (!tr || !tr.applyConstraints) { toast.info("Flash non disponible sur cet appareil."); return; }
    try { await tr.applyConstraints({ advanced: [{ torch: !torch }] }); setTorch(!torch); }
    catch (e) { toast.info("Flash non disponible sur cette caméra."); }
  };

  // ---------- Recording ----------
  const pickMime = () => {
    for (const c of ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"]) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return c;
    }
    return "";
  };

  const beginRecorder = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = pickMime();
    let rec;
    try { rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined); }
    catch (e) { toast.error("Enregistrement non supporté."); return; }
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const type = mime || "video/webm";
      const blob = new Blob(chunksRef.current, { type });
      const ext = type.includes("mp4") ? "mp4" : "webm";
      const file = new File([blob], `studio-${Date.now()}.${ext}`, { type });
      finishCapture(file);
    };
    recorderRef.current = rec;
    rec.start(1000);
    setRecording(true); setPaused(false); setElapsed(0);
    // live music monitor
    if (track?.preview_url) { const a = new Audio(track.preview_url); a.loop = true; a.volume = soundVol; a.play().catch(() => {}); monitorRef.current = a; }
    timerRef.current = setInterval(() => {
      setElapsed((s) => {
        const n = s + 0.2;
        if (n >= durationLimit) { stopRecording(); return durationLimit; }
        return n;
      });
    }, 200);
  };

  const startRecording = () => {
    if (recording) return;
    if (countdown > 0) {
      setCountingNum(countdown);
      const iv = setInterval(() => {
        setCountingNum((c) => {
          if (c <= 1) { clearInterval(iv); beginRecorder(); return 0; }
          return c - 1;
        });
      }, 1000);
    } else beginRecorder();
  };

  const pauseResume = () => {
    const rec = recorderRef.current; if (!rec) return;
    if (paused) { rec.resume(); setPaused(false); monitorRef.current?.play?.().catch(() => {}); }
    else { rec.pause(); setPaused(true); monitorRef.current?.pause?.(); }
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false); setPaused(false);
    monitorRef.current?.pause?.();
    try { recorderRef.current?.stop(); } catch (e) {}
  };

  const finishCapture = async (file) => {
    stopStream();
    setMediaFile(file);
    const url = URL.createObjectURL(file);
    setMediaUrl(url);
    const dur = (await getVideoDuration(file)) || elapsed || 0;
    setDuration(dur);
    setTrim([0, dur || durationLimit]);
    const fr = await captureVideoFrames(file, 8);
    setFrames(fr || []);
    setCover(fr && fr.length ? fr[0] : null);
    setPhase("edit");
  };

  // ---------- Import ----------
  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) return toast.error("Choisissez un fichier vidéo.");
    if (file.size > 60 * 1024 * 1024) return toast.error("Vidéo trop volumineuse (max 60 Mo).");
    const dur = await getVideoDuration(file);
    if (dur && dur > 601) return toast.error("Durée maximale : 10 minutes.");
    stopStream();
    setMediaFile(file);
    setMediaUrl(URL.createObjectURL(file));
    setDuration(dur || 0);
    setTrim([0, dur || 0]);
    const fr = await captureVideoFrames(file, 8);
    setFrames(fr || []);
    setCover(fr && fr.length ? fr[0] : null);
    setPhase("edit");
  };

  // ---------- Edit preview (loop within trim + music) ----------
  useEffect(() => {
    if (phase !== "edit") return;
    const v = editVideoRef.current; if (!v) return;
    const onLoaded = () => { if (!duration && v.duration) { setDuration(v.duration); setTrim([0, v.duration]); } v.currentTime = trim[0]; };
    const onTime = () => { if (v.currentTime >= trim[1] || v.currentTime < trim[0]) { v.currentTime = trim[0]; if (editAudioRef.current) editAudioRef.current.currentTime = 0; } };
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("timeupdate", onTime);
    return () => { v.removeEventListener("loadedmetadata", onLoaded); v.removeEventListener("timeupdate", onTime); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, trim, duration]);

  useEffect(() => { if (editVideoRef.current) editVideoRef.current.volume = origVol; }, [origVol, phase]);
  useEffect(() => { if (editAudioRef.current) editAudioRef.current.volume = soundVol; }, [soundVol, phase, track]);

  const retake = () => {
    setMediaFile(null); setMediaUrl(null); setFrames([]); setCover(null); setPhase("capture");
  };

  // ---------- Publish ----------
  const addTag = (raw) => {
    const clean = (raw || "").replace(/[#\s]/g, "").toLowerCase().slice(0, 30);
    if (!clean) return;
    setTags((p) => (p.includes(clean) || p.length >= 10 ? p : [...p, clean]));
    setTagInput("");
  };
  const onTagKey = (e) => { if (["Enter", " ", ","].includes(e.key)) { e.preventDefault(); addTag(tagInput); } };

  const publish = async () => {
    if (!mediaFile) return;
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(mediaFile);
      if (dataUrl.length > 60_000_000) { setBusy(false); return toast.error("Vidéo trop volumineuse. Choisissez une durée plus courte."); }
      const { data: pub } = await api.post("/wall/publish", {
        image_base64: dataUrl,
        media_type: "video",
        caption,
        cover_base64: cover || "",
        preview_base64: cover || "",
        hashtags: tags,
        visibility,
        filter: filterKey,
        trim_start: trim[0],
        trim_end: trim[1],
        sound: track ? `${track.title} · ${track.artist}` : "",
        sound_key: track?.key || "",
        sound_title: track?.title || "",
        sound_artist: track?.artist || "",
        sound_artwork_url: track?.artwork_url || "",
        sound_preview_url: track?.preview_url || "",
        sound_volume: soundVol,
        original_volume: origVol,
      });
      toast.success("Vidéo publiée !", { description: compressionHint(pub?.video_info) || undefined });
      navigate("/decouvrir");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const close = () => { stopStream(); if (monitorRef.current) monitorRef.current.pause(); navigate(-1); };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="fixed inset-0 z-50 bg-black text-white flex flex-col" data-testid="create-studio">
      <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={onImport} data-testid="studio-import-input" />

      {/* ---------------- CAPTURE ---------------- */}
      {phase === "capture" && (
        <div className="relative flex-1 overflow-hidden">
          <video ref={liveRef} playsInline muted className="absolute inset-0 h-full w-full object-cover" style={{ filter: filterCss(filterKey), transform: facing === "user" ? "scaleX(-1)" : "none" }} data-testid="studio-live-preview" />

          {permError && (
            <div className="absolute inset-0 bg-black/85 grid place-items-center p-6 text-center" data-testid="studio-perm-error">
              <div className="space-y-4 max-w-xs">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-white/10 grid place-items-center"><ZapOff className="h-6 w-6" /></div>
                <p className="text-sm text-white/80">{permError}</p>
                <div className="flex gap-2 justify-center">
                  <Button variant="secondary" className="rounded-xl" onClick={() => startCamera(facing)}>Réessayer</Button>
                  <Button className="rounded-xl" onClick={() => fileRef.current?.click()}><Upload className="h-4 w-4 mr-1" /> Importer</Button>
                </div>
              </div>
            </div>
          )}

          {/* countdown overlay */}
          {countingNum > 0 && (
            <div className="absolute inset-0 grid place-items-center bg-black/40" data-testid="studio-countdown">
              <span className="font-display text-7xl">{countingNum}</span>
            </div>
          )}

          {/* Top bar */}
          <div className="absolute top-0 inset-x-0 p-4 flex items-center justify-between">
            <button onClick={close} className="h-10 w-10 grid place-items-center rounded-full bg-black/40" data-testid="studio-close"><X className="h-5 w-5" /></button>
            {recording && (
              <div className="flex items-center gap-2 rounded-full bg-black/50 px-3 py-1 text-xs">
                <span className="h-2.5 w-2.5 rounded-full bg-[hsl(0_92%_62%)] animate-pulse" />
                <span className="tabular-nums" data-testid="studio-timer">{mmss(elapsed)} / {mmss(durationLimit)}</span>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <button onClick={flip} className="h-10 w-10 grid place-items-center rounded-full bg-black/40" data-testid="studio-flip"><SwitchCamera className="h-5 w-5" /></button>
              <button onClick={toggleTorch} className="h-10 w-10 grid place-items-center rounded-full bg-black/40" data-testid="studio-flash">{torch ? <Zap className="h-5 w-5 text-yellow-300" /> : <ZapOff className="h-5 w-5" />}</button>
            </div>
          </div>

          {/* progress bar */}
          {recording && (
            <div className="absolute top-16 inset-x-4 h-1 rounded-full bg-white/20 overflow-hidden">
              <div className="h-full bg-primary transition-[width] duration-200" style={{ width: `${Math.min(100, (elapsed / durationLimit) * 100)}%` }} />
            </div>
          )}

          {/* Right controls */}
          <div className="absolute right-3 top-1/3 flex flex-col gap-4 items-center">
            <button onClick={() => setShowFilters((s) => !s)} className="flex flex-col items-center gap-1" data-testid="studio-filters-toggle"><span className="h-11 w-11 grid place-items-center rounded-full bg-black/40"><Wand2 className="h-5 w-5" /></span><span className="text-[10px]">Filtres</span></button>
            <button onClick={() => setMusicOpen(true)} className="flex flex-col items-center gap-1" data-testid="studio-music-button"><span className="h-11 w-11 grid place-items-center rounded-full bg-black/40 overflow-hidden">{track?.artwork_url ? <img src={track.artwork_url} alt="" className="h-full w-full object-cover" /> : <Music className="h-5 w-5" />}</span><span className="text-[10px] max-w-[64px] truncate">{track ? track.title : "Son"}</span></button>
          </div>

          {/* Bottom controls */}
          <div className="absolute bottom-0 inset-x-0 pb-6 pt-3 bg-gradient-to-t from-black/70 to-transparent space-y-3">
            {/* filter chips */}
            {showFilters && (
              <div className="flex gap-2 overflow-x-auto no-scrollbar px-4" data-testid="studio-filter-chips">
                {VIDEO_FILTERS.map((f) => (
                  <button key={f.key || "none"} onClick={() => setFilterKey(f.key)} data-testid={`studio-filter-${f.key || "none"}`} className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${filterKey === f.key ? "bg-primary text-primary-foreground" : "bg-white/15"}`}>{f.label}</button>
                ))}
              </div>
            )}

            {/* duration + countdown */}
            {!recording && (
              <div className="flex items-center justify-center gap-2 px-4">
                {DURATIONS.map((d) => (
                  <button key={d.key} onClick={() => setDurationLimit(d.key)} data-testid={`studio-duration-${d.key}`} className={`rounded-full px-3 py-1 text-xs font-medium ${durationLimit === d.key ? "bg-primary text-primary-foreground" : "bg-white/15"}`}>{d.label}</button>
                ))}
                <button onClick={() => setCountdown(COUNTDOWNS[(COUNTDOWNS.indexOf(countdown) + 1) % COUNTDOWNS.length])} data-testid="studio-countdown-toggle" className="rounded-full px-3 py-1 text-xs font-medium bg-white/15 flex items-center gap-1"><Timer className="h-3.5 w-3.5" />{countdown === 0 ? "Off" : `${countdown}s`}</button>
              </div>
            )}

            {/* record row */}
            <div className="flex items-center justify-around px-8">
              <button onClick={() => fileRef.current?.click()} className="flex flex-col items-center gap-1" data-testid="studio-gallery"><span className="h-11 w-11 grid place-items-center rounded-xl bg-white/15"><ImageIcon className="h-5 w-5" /></span><span className="text-[10px]">Galerie</span></button>

              {!recording ? (
                <button onClick={startRecording} className="h-20 w-20 rounded-full grid place-items-center border-4 border-white/70" data-testid="studio-record"><span className="h-16 w-16 rounded-full bg-[hsl(0_92%_62%)]" /></button>
              ) : (
                <button onClick={stopRecording} className="h-20 w-20 rounded-full grid place-items-center border-4 border-white/70" data-testid="studio-stop"><Square className="h-8 w-8 fill-white" /></button>
              )}

              {recording ? (
                <button onClick={pauseResume} className="flex flex-col items-center gap-1" data-testid="studio-pause">
                  <span className="h-11 w-11 grid place-items-center rounded-xl bg-white/15">{paused ? <Play className="h-5 w-5 fill-white" /> : <Pause className="h-5 w-5" />}</span>
                  <span className="text-[10px]">{paused ? "Reprendre" : "Pause"}</span>
                </button>
              ) : (
                <div className="w-11" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---------------- EDIT ---------------- */}
      {phase === "edit" && (
        <div className="flex-1 overflow-y-auto">
          <div className="relative">
            <video ref={editVideoRef} src={mediaUrl} autoPlay loop playsInline className="w-full aspect-[9/16] max-h-[52vh] object-contain bg-black mx-auto" style={{ filter: filterCss(filterKey) }} data-testid="studio-edit-video" />
            {track?.preview_url && <audio ref={editAudioRef} src={track.preview_url} autoPlay loop />}
            <button onClick={retake} className="absolute top-3 left-3 h-10 w-10 grid place-items-center rounded-full bg-black/50" data-testid="studio-edit-back"><ArrowLeft className="h-5 w-5" /></button>
          </div>

          <div className="p-4 space-y-5 bg-background text-foreground rounded-t-2xl -mt-4 relative">
            {/* Trim */}
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-1.5"><Scissors className="h-4 w-4 text-primary" /> Découper — {mmss(trim[0])} → {mmss(trim[1])}</p>
              <Slider min={0} max={Math.max(duration, 1)} step={0.1} value={trim} onValueChange={(v) => setTrim([Math.min(v[0], v[1] - 0.5), Math.max(v[1], v[0] + 0.5)])} data-testid="studio-trim-slider" />
            </div>

            {/* Filters */}
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-1.5"><Wand2 className="h-4 w-4 text-primary" /> Filtre</p>
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {VIDEO_FILTERS.map((f) => (
                  <button key={f.key || "none"} onClick={() => setFilterKey(f.key)} className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${filterKey === f.key ? "bg-primary text-primary-foreground" : "bg-secondary"}`} data-testid={`studio-edit-filter-${f.key || "none"}`}>{f.label}</button>
                ))}
              </div>
            </div>

            {/* Music + volumes */}
            <div className="space-y-3">
              <p className="text-sm font-medium flex items-center gap-1.5"><Music className="h-4 w-4 text-primary" /> Musique</p>
              {track ? (
                <div className="flex items-center gap-3 rounded-xl border border-border p-2">
                  {track.artwork_url && <img src={track.artwork_url} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{track.title}</p><p className="text-xs text-muted-foreground truncate">{track.artist}</p></div>
                  <Button variant="ghost" size="sm" onClick={() => setMusicOpen(true)} data-testid="studio-edit-change-music">Changer</Button>
                  <button onClick={() => setTrack(null)} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-secondary"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <Button variant="outline" className="w-full rounded-xl" onClick={() => setMusicOpen(true)} data-testid="studio-edit-add-music"><Music className="h-4 w-4 mr-1" /> Ajouter une musique</Button>
              )}
              <div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1"><span className="flex items-center gap-1"><Music className="h-3.5 w-3.5" /> Volume musique</span><span>{Math.round(soundVol * 100)}%</span></div>
                <Slider min={0} max={1} step={0.05} value={[soundVol]} onValueChange={(v) => setSoundVol(v[0])} data-testid="studio-sound-vol" />
              </div>
              <div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1"><span className="flex items-center gap-1"><Mic2 className="h-3.5 w-3.5" /> Volume vidéo (son original)</span><span>{Math.round(origVol * 100)}%</span></div>
                <Slider min={0} max={1} step={0.05} value={[origVol]} onValueChange={(v) => setOrigVol(v[0])} data-testid="studio-orig-vol" />
              </div>
            </div>

            {/* Cover */}
            {frames.length > 1 && (
              <div className="space-y-2">
                <p className="text-sm font-medium flex items-center gap-1.5"><ImageIcon className="h-4 w-4 text-primary" /> Miniature</p>
                <div className="flex gap-2 overflow-x-auto no-scrollbar">
                  {frames.map((f, i) => (
                    <button key={i} onClick={() => setCover(f)} className={`shrink-0 h-24 w-16 rounded-lg overflow-hidden border-2 ${cover === f ? "border-primary" : "border-transparent"}`} data-testid={`studio-cover-${i}`}><img src={f} alt="" className="h-full w-full object-cover" style={{ filter: filterCss(filterKey) }} /></button>
                  ))}
                </div>
              </div>
            )}

            <Button className="w-full rounded-xl h-12" onClick={() => setPhase("publish")} data-testid="studio-next"><Check className="h-4 w-4 mr-1" /> Suivant</Button>
          </div>
        </div>
      )}

      {/* ---------------- PUBLISH ---------------- */}
      {phase === "publish" && (
        <div className="flex-1 overflow-y-auto bg-background text-foreground">
          <div className="p-4 space-y-5">
            <div className="flex items-center gap-2">
              <button onClick={() => setPhase("edit")} className="h-10 w-10 grid place-items-center rounded-xl bg-secondary" data-testid="studio-publish-back"><ArrowLeft className="h-5 w-5" /></button>
              <h1 className="font-display text-xl">Publier la vidéo</h1>
            </div>

            <div className="flex gap-3">
              <div className="h-40 w-24 rounded-xl overflow-hidden bg-black shrink-0">
                {cover ? <img src={cover} alt="" className="h-full w-full object-cover" style={{ filter: filterCss(filterKey) }} /> : <video src={mediaUrl} className="h-full w-full object-cover" style={{ filter: filterCss(filterKey) }} muted />}
              </div>
              <div className="flex-1">
                <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Décrivez votre vidéo…" className="rounded-xl h-40 resize-none" maxLength={280} data-testid="studio-caption" />
              </div>
            </div>

            {/* hashtags */}
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-1.5"><Hash className="h-4 w-4 text-primary" /> Hashtags</p>
              <div className="relative">
                <Hash className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={onTagKey} onBlur={() => tagInput && addTag(tagInput)} placeholder="Ajoutez un hashtag puis Entrée" className="pl-9 rounded-xl" maxLength={30} data-testid="studio-hashtag-input" />
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((tg) => <span key={tg} className="flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-medium">#{tg}<button onClick={() => setTags(tags.filter((x) => x !== tg))}><X className="h-3 w-3" /></button></span>)}
                </div>
              )}
            </div>

            {/* visibility */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Qui peut voir ?</p>
              <div className="grid grid-cols-3 gap-2" data-testid="studio-visibility">
                {VISIBILITIES.map((v) => {
                  const Icon = v.icon;
                  return (
                    <button key={v.key} onClick={() => setVisibility(v.key)} data-testid={`studio-visibility-${v.key}`} className={`rounded-xl border p-3 flex flex-col items-center gap-1 text-xs font-medium transition-colors ${visibility === v.key ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>
                      <Icon className="h-5 w-5" /> {v.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground">Toutes les publications publiques sont gratuites. Pour vendre un média, envoyez-le en message privé depuis une conversation.</p>
            </div>

            <Button className="w-full rounded-xl h-12 neon-glow-blue-sm" onClick={publish} disabled={busy} data-testid="studio-publish"><Check className="h-4 w-4 mr-2" /> {busy ? "Publication…" : "Publier"}</Button>
          </div>
        </div>
      )}

      <MusicPicker open={musicOpen} onOpenChange={setMusicOpen} onSelect={(tk) => { setTrack(tk); if (monitorRef.current) { monitorRef.current.pause(); } if (recording && tk?.preview_url) { const a = new Audio(tk.preview_url); a.loop = true; a.volume = soundVol; a.play().catch(() => {}); monitorRef.current = a; } }} />
    </div>
  );
};

export default CreateStudio;
